import { ADWORDS_DISPLAY_NAME, FACEBOOK_DISPLAY_NAME } from '../../utils/reportsConsts';
import { getAdnetworkDisplayName, normalizeSourceDisplayName } from '../../utils/adNetworks';

export const MIN_AMOUNT_ALL_SOURCES = 5;

const TIKTOK_PREFIX = 'tiktok';

const isObject = value => typeof value === 'object' && value !== null;

// there is no single canonical TikTok display_name to match against - so we implemented a prefix matching
const isTiktokSource = source => typeof source === 'string' && source.toLowerCase().startsWith(TIKTOK_PREFIX);

// source_mapping is hand-authored Python config with no schema enforcement, so each entry is
// normalized to an object (just enough to safely read hasOwnProperty/properties off it -
// not a genuine plain-object check, which isn't needed here) before use.
const asRuleObject = value => (isObject(value) ? value : {});

// Shared by fieldDefaultingRules.js's creativeExplore (ruleKey 'is_default') and
// fieldVisibilityRules.js's getSourceMappedVisibility (ruleKey 'is_visible') so the
// facebook/adwords/tiktok/other/all_sources resolution order can't drift between the two.
export const resolveSourceMapping = (sourceMapping, rawSelectedSources, ruleKey, fallback) => {
    const safeSourceMapping = isObject(sourceMapping) ? sourceMapping : {};
    const facebookRules = asRuleObject(safeSourceMapping.facebook);
    const adwordsRules = asRuleObject(safeSourceMapping.adwords);
    const tiktokRules = asRuleObject(safeSourceMapping.tiktok);
    const otherRules = asRuleObject(safeSourceMapping.other);
    const allSourcesRules = asRuleObject(safeSourceMapping.all_sources);
    const anyRules = asRuleObject(safeSourceMapping.any);

    const hasAnyRule = anyRules.hasOwnProperty(ruleKey);
    const hasEmptySelection = !rawSelectedSources?.length;

    if (ruleKey === 'is_default' && hasEmptySelection) {
        return fallback;
    }

    if (hasAnyRule) {
        return anyRules[ruleKey];
    }

    // no sources selected means the caller isn't filtering by source at all
    // (e.g. Creative Gallery always passes []) - per-source/other rules don't apply
    if (hasEmptySelection) {
        return fallback;
    }

    const hasAllSourcesRule = allSourcesRules.hasOwnProperty(ruleKey);
    const hasFacebookRule = facebookRules.hasOwnProperty(ruleKey);
    const hasAdwordsRule = adwordsRules.hasOwnProperty(ruleKey);
    const hasTiktokRule = tiktokRules.hasOwnProperty(ruleKey);
    const hasOtherRule = otherRules.hasOwnProperty(ruleKey);

    if (!hasAllSourcesRule && !hasFacebookRule && !hasAdwordsRule && !hasTiktokRule && !hasOtherRule) {
        return fallback;
    }

    const selectedSources = rawSelectedSources.map(normalizeSourceDisplayName);

    if (hasAllSourcesRule && selectedSources.length >= MIN_AMOUNT_ALL_SOURCES) {
        return allSourcesRules[ruleKey];
    }

    const containsFacebook = selectedSources.includes(getAdnetworkDisplayName('facebook', FACEBOOK_DISPLAY_NAME));
    const containsAdwords = selectedSources.includes(getAdnetworkDisplayName('adwords', ADWORDS_DISPLAY_NAME));
    const containsTiktok = selectedSources.some(isTiktokSource);

    if (!containsFacebook && !containsAdwords && !containsTiktok && hasOtherRule) {
        return otherRules[ruleKey];
    }

    let result = false;
    if (containsFacebook && hasFacebookRule) {
        result ||= facebookRules[ruleKey];
    }

    if (containsAdwords && hasAdwordsRule) {
        result ||= adwordsRules[ruleKey];
    }

    if (containsTiktok && hasTiktokRule) {
        result ||= tiktokRules[ruleKey];
    }

    return result;
};
